"use client";

// Inspired by react-hot-toast library
import * as React from "react";

import type { ToastActionElement, ToastProps } from "@shared/src/components/ui/toast";

const TOAST_LIMIT = 10;
const TOAST_REMOVE_DELAY = 1000;

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  onReload?: () => void;
  permanent?: boolean;
  loading?: boolean;
  dismissible?: boolean;
  position?: "top-center" | "top-left" | "top-right" | "bottom-left" | "bottom-right" | "bottom-center";
};

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType["ADD_TOAST"];
      toast: ToasterToast;
    }
  | {
      type: ActionType["UPDATE_TOAST"];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType["DISMISS_TOAST"];
      toastId?: ToasterToast["id"];
    }
  | {
      type: ActionType["REMOVE_TOAST"];
      toastId?: ToasterToast["id"];
    };

interface State {
  toasts: {
    "top-center": ToasterToast[];
    "top-left": ToasterToast[];
    "top-right": ToasterToast[];
    "bottom-left": ToasterToast[];
    "bottom-right": ToasterToast[];
    "bottom-center": ToasterToast[];
  };
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string, duration: number = TOAST_REMOVE_DELAY) => {
  // Clear existing timeout if any
  if (toastTimeouts.has(toastId)) {
    clearTimeout(toastTimeouts.get(toastId));
    toastTimeouts.delete(toastId);
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    });
  }, duration);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST": {
      const position = action.toast.position || "top-center";
      const positionToasts = state.toasts[position];

      // Improved duplicate check
      const isDuplicate = Object.values(state.toasts).some(toasts =>
        toasts.some(
          t =>
            t.title === action.toast.title &&
            t.description === action.toast.description &&
            t.variant === action.toast.variant &&
            (t.open || !toastTimeouts.has(t.id)) &&
            t.permanent === action.toast.permanent,
        ),
      );

      if (isDuplicate) {
        return state;
      }

      // Filter out toasts that are being removed for this position
      const activeToasts = positionToasts.filter(t => t.open || t.permanent || !toastTimeouts.has(t.id));

      return {
        ...state,
        toasts: {
          ...state.toasts,
          [position]: [action.toast, ...positionToasts].slice(
            0,
            TOAST_LIMIT + (positionToasts.length - activeToasts.length),
          ),
        },
      };
    }

    case "UPDATE_TOAST": {
      const position = action.toast.position || "top-center";
      return {
        ...state,
        toasts: {
          ...state.toasts,
          [position]: state.toasts[position].map(t => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
        },
      };
    }

    case "DISMISS_TOAST": {
      const { toastId } = action;
      const updatedToasts = { ...state.toasts };

      // Helper function to process toasts in a position
      const processPositionToasts = (position: keyof State["toasts"]) => {
        const toasts = state.toasts[position];
        return toasts.map(t => {
          // Handle both permanent and non-permanent toasts when explicitly dismissed
          if ((toastId && t.id === toastId) || !toastId) {
            if (t.duration !== Infinity) {
              addToRemoveQueue(t.id, t.duration);
            }
            return { ...t, open: false };
          }
          return t;
        });
      };

      // Process all positions
      Object.keys(state.toasts).forEach(position => {
        updatedToasts[position as keyof State["toasts"]] = processPositionToasts(position as keyof State["toasts"]);
      });

      return {
        ...state,
        toasts: updatedToasts,
      };
    }

    case "REMOVE_TOAST": {
      const { toastId } = action;
      const updatedToasts = { ...state.toasts };

      // Clear timeout when removing toast
      if (toastId && toastTimeouts.has(toastId)) {
        clearTimeout(toastTimeouts.get(toastId));
        toastTimeouts.delete(toastId);
      }

      // Remove toast from all positions if no ID specified, or remove specific toast
      Object.keys(state.toasts).forEach(position => {
        updatedToasts[position as keyof State["toasts"]] = state.toasts[position as keyof State["toasts"]].filter(t =>
          toastId ? t.id !== toastId : t.permanent || t.duration === Infinity,
        );
      });

      return {
        ...state,
        toasts: updatedToasts,
      };
    }

    default:
      return state;
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = {
  toasts: {
    "top-center": [],
    "top-left": [],
    "top-right": [],
    "bottom-left": [],
    "bottom-right": [],
    "bottom-center": [],
  },
};

export function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach(listener => {
    listener(memoryState);
  });
}

type Toast = Omit<ToasterToast, "id">;

function toast({ dismissible = true, ...props }: Toast) {
  const id = genId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

  const onOpenChange = props.onOpenChange;

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      dismissible,
      duration: props.permanent ? Infinity : props.duration ?? TOAST_REMOVE_DELAY,
      onOpenChange: open => {
        if (!dismissible) return;
        onOpenChange?.(open);
        if (!open) dismiss();
      },
    },
  });

  return {
    id: id,
    dismiss,
    update,
  };
}

toast.dismiss = () => dispatch({ type: "DISMISS_TOAST" });

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  };
}

export { useToast, toast };
